/**
 * ConfirmDialog Component
 *
 * A reusable confirmation dialog following the Portfolistic design system.
 * Replaces native window.confirm() with a styled, accessible modal.
 *
 * Features:
 * - Multiple variants (danger, warning, info)
 * - Custom icons and messages
 * - Keyboard navigation (Escape to cancel, Enter to confirm)
 * - Focus trap for accessibility
 * - Backdrop click to dismiss
 *
 * Usage:
 * <ConfirmDialog
 *   isOpen={showConfirm}
 *   onConfirm={handleDelete}
 *   onCancel={() => setShowConfirm(false)}
 *   title="Delete Goal"
 *   message="Are you sure you want to delete this goal? This action cannot be undone."
 *   variant="danger"
 * />
 */

import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle,
  Trash2,
  Info,
  AlertCircle,
  X,
  Check,
} from 'lucide-react';
import Button from './Button';

/**
 * Variant configurations for different dialog types
 */
const variants = {
  danger: {
    icon: Trash2,
    iconBg: 'bg-error-500/20',
    iconColor: 'text-error-400',
    confirmButton: 'danger',
    confirmLabel: 'Delete',
  },
  warning: {
    icon: AlertTriangle,
    iconBg: 'bg-warning-500/20',
    iconColor: 'text-warning-400',
    confirmButton: 'warning',
    confirmLabel: 'Continue',
  },
  info: {
    icon: Info,
    iconBg: 'bg-secondary-500/20',
    iconColor: 'text-secondary-400',
    confirmButton: 'primary',
    confirmLabel: 'Confirm',
  },
  confirm: {
    icon: AlertCircle,
    iconBg: 'bg-primary-500/20',
    iconColor: 'text-primary-400',
    confirmButton: 'primary',
    confirmLabel: 'Confirm',
  },
};

/**
 * ConfirmDialog Component
 */
const ConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  variant = 'confirm',
  confirmLabel,
  cancelLabel = 'Cancel',
  loading = false,
  icon: CustomIcon,
  children,
}) => {
  const dialogRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  const config = variants[variant] || variants.confirm;
  const IconComponent = CustomIcon || config.icon;
  const finalConfirmLabel = confirmLabel || config.confirmLabel;

  // Handle escape key
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && !loading) {
        onCancel();
      }
      // Enter key confirms when not loading
      if (e.key === 'Enter' && !loading && document.activeElement !== confirmButtonRef.current) {
        e.preventDefault();
        onConfirm();
      }
    },
    [onCancel, onConfirm, loading]
  );

  // Focus trap
  useEffect(() => {
    if (isOpen) {
      // Store previous focus
      previousActiveElement.current = document.activeElement;

      // Focus the dialog
      dialogRef.current?.focus();

      // Add event listener
      document.addEventListener('keydown', handleKeyDown);

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';

        // Restore focus
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, handleKeyDown]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" aria-hidden="true" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative glass rounded-xl shadow-2xl
                   w-full max-w-md transform transition-all duration-200 ease-out
                   animate-in fade-in zoom-in-95"
      >
        {/* Close button */}
        <button
          onClick={onCancel}
          disabled={loading}
          className="absolute top-4 right-4 p-1 rounded-lg text-gray-400
                     hover:text-white hover:bg-white/10 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close dialog"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`p-3 rounded-full ${config.iconBg}`}>
              <IconComponent className={`w-8 h-8 ${config.iconColor}`} />
            </div>
          </div>

          {/* Title */}
          <h2
            id="confirm-dialog-title"
            className="text-xl font-semibold text-white text-center mb-2"
          >
            {title}
          </h2>

          {/* Message */}
          {message && (
            <p
              id="confirm-dialog-message"
              className="text-gray-400 text-center mb-6"
            >
              {message}
            </p>
          )}

          {/* Custom content */}
          {children && <div className="mb-6">{children}</div>}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={loading}
              className="flex-1"
            >
              {cancelLabel}
            </Button>
            <Button
              ref={confirmButtonRef}
              variant={config.confirmButton}
              onClick={onConfirm}
              loading={loading}
              className="flex-1"
            >
              {loading ? 'Processing...' : finalConfirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  /** Whether the dialog is open */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when confirm is clicked */
  onConfirm: PropTypes.func.isRequired,
  /** Callback when cancel is clicked or dialog is dismissed */
  onCancel: PropTypes.func.isRequired,
  /** Dialog title */
  title: PropTypes.string.isRequired,
  /** Dialog message/description */
  message: PropTypes.string,
  /** Visual variant */
  variant: PropTypes.oneOf(['danger', 'warning', 'info', 'confirm']),
  /** Custom confirm button label */
  confirmLabel: PropTypes.string,
  /** Custom cancel button label */
  cancelLabel: PropTypes.string,
  /** Show loading state on confirm button */
  loading: PropTypes.bool,
  /** Custom icon component */
  icon: PropTypes.elementType,
  /** Additional content to render in the dialog body */
  children: PropTypes.node,
};

/**
 * Hook for managing confirm dialog state
 *
 * Usage:
 * const { isOpen, openDialog, closeDialog, confirmAction } = useConfirmDialog();
 *
 * const handleDelete = async () => {
 *   openDialog();
 * };
 *
 * const handleConfirm = async () => {
 *   await deleteItem();
 *   closeDialog();
 * };
 */
export const useConfirmDialog = (onConfirm) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);

  const openDialog = useCallback((itemData = null) => {
    setData(itemData);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    if (!loading) {
      setIsOpen(false);
      setData(null);
    }
  }, [loading]);

  const confirmAction = useCallback(async () => {
    if (onConfirm) {
      setLoading(true);
      try {
        await onConfirm(data);
        setIsOpen(false);
        setData(null);
      } finally {
        setLoading(false);
      }
    }
  }, [onConfirm, data]);

  return {
    isOpen,
    loading,
    data,
    openDialog,
    closeDialog,
    confirmAction,
  };
};

/**
 * Pre-configured delete confirmation dialog
 */
export const DeleteConfirmDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  itemName = 'this item',
  loading,
}) => (
  <ConfirmDialog
    isOpen={isOpen}
    onConfirm={onConfirm}
    onCancel={onCancel}
    title="Delete Item"
    message={`Are you sure you want to delete ${itemName}? This action cannot be undone.`}
    variant="danger"
    confirmLabel="Delete"
    loading={loading}
  />
);

DeleteConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  itemName: PropTypes.string,
  loading: PropTypes.bool,
};

/**
 * Pre-configured unsaved changes warning dialog
 */
export const UnsavedChangesDialog = ({
  isOpen,
  onConfirm,
  onCancel,
  loading,
}) => (
  <ConfirmDialog
    isOpen={isOpen}
    onConfirm={onConfirm}
    onCancel={onCancel}
    title="Unsaved Changes"
    message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost."
    variant="warning"
    confirmLabel="Leave"
    cancelLabel="Stay"
    loading={loading}
  />
);

UnsavedChangesDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ConfirmDialog;
